// 入力フォームの初期値 (initialValues属性にセットする)
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomerPagePath } from 'views/constants/enums/pageTitle';
import { useSmsCertificateMutation, useSendSmsMutation } from 'views/utils/graphql/graphql';
import { SendSMSParam } from 'views/components/templates/sendSMS';
import { useToast } from 'views/components/atoms/toast/hook';
import { ToastMessageLevel } from 'views/components/atoms/toast';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useState } from 'react';

type LoginResult = {
  provisionalToken: string;
  phoneNumber: string;
};

export const useSendSMS = () => {
  const [SmsCertificatelData, SmsCertificate] = useSmsCertificateMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toastProps, showToast } = useToast();
  const { provisionalToken: initialProvisionalToken, phoneNumber } = location.state as LoginResult;

  const { onSendSMS, provisionalToken } = useOnRetransmission(initialProvisionalToken, showToast);

  if (!provisionalToken) {
    navigate(CustomerPagePath.Login);
  }

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const onSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const ret = await SmsCertificate({
          input: {
            Type: 1,
            AuthenticationCode: values.authenticationCode,
            ProvisionalToken: provisionalToken,
          },
        });

        if (ret.data && ret.data.SMSCertificate?.Code !== 0) {
          showToast(ret.data.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
        } else if (ret.data?.SMSCertificate?.Code === 0) {
          sessionStorage.setItem('token', ret.data.SMSCertificate.Token!);
          navigate(CustomerPagePath.Home);
        }
      },
    },
    [provisionalToken, showToast, navigate],
  );

  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit,
    validate,
  });

  return { formik, phoneNumber, onSendSMS, toastProps };
};

const useOnRetransmission = (
  initialProvisionalToken: string,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [provisionalToken, setProvisionalToken] = useState<string>(initialProvisionalToken);
  const [sendSMSData, sendSMS] = useSendSmsMutation();

  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await sendSMS({ input: { Type: 1, ProvisionalToken: provisionalToken } });
        if (res.data && res.data.SendSMS?.Code !== 0) {
          showToast(res.data.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          showToast('認証コードを送信しました', ToastMessageLevel.Info);
        }
      },
    },
    [provisionalToken, setProvisionalToken, showToast, sendSMS],
  );

  return {
    onSendSMS,
    provisionalToken,
  };
};
